// EditorContent.jsx
// Main editor content layout (header, toolbar, editor, margin rail)

import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import DocumentHeader from './DocumentHeader';
import {Toolbar} from './toolbar';

/**
 * Editor content layout component.
 *
 * Renders a scrollable 3-column grid:
 *   left spacer (1fr) | center (header + body + citations) | right margin rail (1fr)
 *
 * The grid switches via a CSS @container query (named "pm-editor" on the scroll
 * container). At ≥ 1053px the right track gets a 320px floor so the full rail is
 * always visible; below that both outer tracks are minmax(48px, 1fr) and shrink
 * symmetrically — the rail's own ResizeObserver handles the full↔icon switch.
 *
 * @param {Object} props
 * @param {boolean} props.showDocumentHeader - Whether to show document header
 * @param {boolean} props.showToolbar - Whether to show toolbar
 * @param {string} props.documentId - Document ID for header
 * @param {Function} props.onHeaderChange - Callback when header changes
 * @param {React.ReactNode} props.documentHeader - Optional custom document header component
 * @param {Object} props.view - ProseMirror EditorView instance
 * @param {Object} props.provider - Yjs provider instance
 * @param {number} props.viewTick - View update counter for re-rendering
 * @param {React.RefObject} props.editorRef - Ref for editor mount point
 * @param {React.ReactNode} props.marginRail - Optional right margin rail component
 * @param {React.ReactNode} props.citations - Optional citations/references section below editor
 * @param {import('yjs').Map|null} props.yDocMeta - Y.Map for document metadata
 * @param {import('yjs').Doc|null} props.ydoc - Y.Doc for transactions
 * @param {number} props.headerMetaTick - Bumps when documentMeta is hydrated from API
 */
export default function EditorContent({
    showDocumentHeader,
    showToolbar,
    documentId,
    onHeaderChange,
    documentHeader,
    view,
    provider,
    viewTick,
    editorRef,
    marginRail,
    citations,
    yDocMeta,
    ydoc,
    headerMetaTick = 0,
    collaboration = false,
    editorMode = 'full',
}) {
    const headerContent = showDocumentHeader
        ? (documentHeader || (
            <DocumentHeader
                documentId={documentId}
                onChange={onHeaderChange}
                yDocMeta={yDocMeta}
                ydoc={ydoc}
                headerMetaTick={headerMetaTick}
            />
        ))
        : null;

    return (
        <Stack sx={{height: '100%', overflow: 'hidden'}}>
            {showToolbar && (
                <Toolbar
                    view={view}
                    provider={provider}
                    collaboration={collaboration}
                    editorMode={editorMode}
                    key={viewTick}
                />
            )}

            {/* Scrollable area — contains the 3-column page grid */}
            <Box
                className="pm-editor-scroll-container"
                sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                }}
            >
                <Box className="pm-editor-page-grid">
                    {/* Left gutter — empty spacer for visual centering */}
                    <Box className="pm-editor-left-rail" aria-hidden="true" />

                    {/* Center column: doc header + ProseMirror body + citations */}
                    <Box className="pm-editor-center-col">
                        {headerContent && (
                            <Box className="pm-editor-header-inner">
                                {headerContent}
                            </Box>
                        )}
                        <Box ref={editorRef} className="pm-editor-body" />
                        {citations && (
                            <Box className="pm-editor-citations">
                                {citations}
                            </Box>
                        )}
                    </Box>

                    {/* Right margin rail */}
                    <Box className="pm-editor-right-rail">
                        {marginRail}
                    </Box>
                </Box>
            </Box>
        </Stack>
    );
}
